import type {FinanceStackOption} from 'slices/RoiCalculator/components/FinanceStackSelect'
import {FINANCE_STACKS, HOURS_PER_WORK_DAY} from 'slices/RoiCalculator/constants'
import type {RoiCalculatorLabels} from 'slices/RoiCalculator/types'
import {formatNumber} from 'slices/RoiCalculator/utils'
import {createTranslator} from 'src/common/i18n'

export type RoiCalculatorTranslations = {
  labels: RoiCalculatorLabels
  financeStackOptions: FinanceStackOption[]
}

export const getTranslations = (locale: string): RoiCalculatorTranslations => {
  const translate = createTranslator(locale)

  return {
    labels: {
      presetsLabel: translate('roiCalculator.aria.presets'),
      presets: {
        startup: translate('roiCalculator.presets.startup'),
        scaleUp: translate('roiCalculator.presets.scaleUp'),
        enterprise: translate('roiCalculator.presets.enterprise'),
      },
      volumes: {
        cards: translate('roiCalculator.volumes.cards'),
        invoices: translate('roiCalculator.volumes.invoices'),
        reimbursements: translate('roiCalculator.volumes.reimbursements'),
      },
      hourlyRate: translate('roiCalculator.hourlyRate'),
      minutesPerTransaction: translate('roiCalculator.minutesPerTransaction'),
      financeStack: translate('roiCalculator.financeStack'),
      hoursSaved: translate('roiCalculator.hoursSaved'),
      formatHours: (hours) =>
        translate('roiCalculator.hours', {count: Math.round(hours), formattedCount: formatNumber(hours, locale)}),
      moneySaved: translate('roiCalculator.moneySaved'),
      breakdown: {
        cards: translate('roiCalculator.breakdown.cards'),
        invoices: translate('roiCalculator.breakdown.invoices'),
        reimbursements: translate('roiCalculator.breakdown.reimbursements'),
      },
      shareResult: translate('roiCalculator.shareResult'),
      hints: {
        hourlyRate: translate('roiCalculator.hints.hourlyRate'),
        minutesPerTransaction: translate('roiCalculator.hints.minutesPerTransaction'),
      },
      results: {
        heading: translate('roiCalculator.results.heading'),
        monthly: translate('roiCalculator.results.monthly'),
        yearly: translate('roiCalculator.results.yearly'),
        perMonth: translate('roiCalculator.results.perMonth'),
        perYear: translate('roiCalculator.results.perYear'),
        perMonthShort: translate('roiCalculator.results.perMonthShort'),
        perYearShort: translate('roiCalculator.results.perYearShort'),
        perHourShort: translate('roiCalculator.results.perHourShort'),
        days: translate('roiCalculator.results.days'),
        workDays: translate('roiCalculator.results.workDays', {hours: HOURS_PER_WORK_DAY}),
        showFullResults: translate('roiCalculator.results.showFullResults'),
        hideFullResults: translate('roiCalculator.results.hideFullResults'),
      },
    },
    financeStackOptions: FINANCE_STACKS.map((stack) => ({
      value: stack,
      label: translate(`roiCalculator.financeStackOptions.${stack}`),
    })),
  }
}
